import { useRouter } from 'next/router';

import { Box, Heading, Paragraph } from '@zillow/constellation-10';

import { useGetListingDetails, useGetListingRecommendations } from '../../../../hooks/queries/properties';
import propertyUtils from '../../../../utils/propertyUtils';

import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../constants/RecommendationsConstants';

import WizardTipCardContainer from '../../../../../srcLegacy/shared/components/WizardTipCardContainer';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import BathroomsFormField from '../../../PostingPath/BathroomsFormField';
import BedroomsFormField from '../../../PostingPath/BedroomsFormField';
import RoomSquareFootageFormField from '../../../PostingPath/RoomSquareFootageFormField';
import SquareFootageFormField from '../../../PostingPath/SquareFootageFormField';
import PageWizard from '../../../Wizard/PageWizard';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const PropertyInfo = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const squareFootageRec = recommendationsBySection[REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION]?.[RECS.SQUARE_FOOTAGE];

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      hideBackButton={!isRoomForRent}
    >
      <Box css={wizardFlexContainerStyles}>
        <Box>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
              {isRoomForRent ? 'Confirm some facts about the room and property' : 'Let’s start creating your listing'}
            </Heading>

            {!isRoomForRent && (
              <Paragraph css={wizardParagraphStyles}>Add or review details about your property’s size</Paragraph>
            )}

            {isRoomForRent && <RoomSquareFootageFormField />}

            <SquareFootageFormField />

            <BedroomsFormField />

            <BathroomsFormField />
          </Box>
        </Box>

        {isRoomForRent && (
          <WizardTipCardContainer
            header="Including square footage"
            showDesktopViewBreakpoint={showDesktopViewBreakpoint}
          >
            <Paragraph css={{ marginBottom: '200' }}>
              Providing the room’s square footage helps renters determine whether the space will work for them.
            </Paragraph>
            <Paragraph>To measure a room’s square footage, multiply the room’s length by its width.</Paragraph>{' '}
          </WizardTipCardContainer>
        )}

        {!isRoomForRent && squareFootageRec?.outcomeStats && (
          <WizardTipCardContainer
            header="Why show square footage?"
            showDesktopViewBreakpoint={showDesktopViewBreakpoint}
          >
            <Paragraph>
              Providing renters with the rental’s square footage can result in up to {squareFootageRec?.outcomeStats}%
              more contacts, based on data for similarly priced listings.
            </Paragraph>

            <Paragraph textStyle="body-xs" color="text.subtle" css={{ marginBlock: '400' }}>
              {squareFootageRec?.source?.tip}
            </Paragraph>
          </WizardTipCardContainer>
        )}
      </Box>
    </PageWizard>
  );
};

export default PropertyInfo;
